import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;

/* ── helpers ── */
const toSlug = (title) =>
  title.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const calcReadTime = (content) => {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

/* ── small reusable field label ── */
const FieldLabel = ({ htmlFor, children, hint }) => (
  <div className="flex items-baseline justify-between mb-1.5">
    <label htmlFor={htmlFor} className="text-sm font-semibold text-gray-700">
      {children}
    </label>
    {hint && <span className="text-xs text-gray-400">{hint}</span>}
  </div>
);

/* ── toast notification ── */
const Toast = ({ message, type = "success", onClose }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5
                rounded-2xl shadow-xl text-sm font-medium text-white
                animate-[slideUp_0.3s_ease-out]
                ${type === "success" ? "bg-[#5b6f00]" : "bg-red-500"}`}
  >
    <span>{type === "success" ? "✅" : "❌"}</span>
    {message}
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100 text-lg leading-none">×</button>
  </div>
);

/* ════════════════════════════════
   Main component
════════════════════════════════ */
const AdminBlog = ({ onBack , currentUser }) => {
  const navigate = useNavigate();

  /* ── auth check ── */
  // const token   = localStorage.getItem("token");
  const isAdmin = currentUser?.role === "admin";
  /* ── form state ── */
  const [form, setForm] = useState({ title: "", cover: "", excerpt: "", content: "" });
  const [derived, setDerived] = useState({ slug: "", readTime: "—" });

  /* ── ui state ── */
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState(null);  // { message, type }
  const [preview, setPreview]   = useState(false);
  const toastTimer              = useRef(null);

  /* ── auto-derive slug + readTime whenever form changes ── */
  useEffect(() => {
    setDerived({
      slug:     form.title ? toSlug(form.title) : "",
      readTime: form.content ? calcReadTime(form.content) : "—",
    });
  }, [form.title, form.content]);

  /* cleanup toast timer on unmount */
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* ── access guard ── */
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#fdfcf7] flex flex-col items-center justify-center gap-4">
        <span className="text-5xl">🚫</span>
        <p className="text-lg font-semibold text-gray-700">Admin access only</p>
        <button
          onClick={() => navigate("/blogs")}
          className="text-sm text-[#5b6f00] hover:underline underline-offset-2"
        >
          ← Return to blogs
        </button>
      </div>
    );
  }

  /* ── handlers ── */
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  // 🔥 FIX — get token at runtime
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  console.log("TOKEN USED:", token);

  if (!token) {
    showToast("Please login again", "error");
    setLoading(false);
    return;
  }

  const payload = {
    ...form,
    slug: derived.slug,
    readTime: derived.readTime,
    author: "Project X",
    status: "published",
  };

  try {
    const res = await fetch(`${API}/api/blogs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // ✅ correct now
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error();

    showToast("Blog published successfully!");
    setForm({ title: "", cover: "", excerpt: "", content: "" });

    setTimeout(() => navigate("/blogs"), 1800);
  } catch {
    showToast("Failed to publish. Please try again.", "error");
  } finally {
    setLoading(false);
  }
};

  /* ── preview parser (simple) ── */
  const previewBlocks = form.content
    .split("\n\n")
    .map((b, i) => {
      const t = b.trim();
      if (!t) return null;
      if (t.startsWith("img:"))  return <img key={i} src={t.replace("img:", "").trim()} alt="" className="w-full rounded-xl my-2 object-cover max-h-64" />;
      if (t.startsWith("## "))   return <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-1 border-l-4 border-[#5b6f00] pl-3">{t.replace("## ", "")}</h2>;
      if (t.startsWith("# "))    return <h1 key={i} className="text-2xl font-extrabold text-gray-900 mt-6 mb-1">{t.replace("# ", "")}</h1>;
      return <p key={i} className="text-[15px] leading-relaxed text-gray-700">{t}</p>;
    })
    .filter(Boolean);

  /* ════════════════════════════════
     Render
  ════════════════════════════════ */
  return (
    <section className="bg-[#fdfcf7] min-h-screen px-4 pt-32 pb-28">

      {/* toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-3xl mx-auto">

        {/* ── page header ── */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-[#5b6f00] opacity-70">
              CMS &nbsp;/&nbsp; Blogs
            </span>
            <h1 className="text-2xl font-extrabold text-gray-900 mt-0.5">
              Publish a New Article
            </h1>
          </div>

          <button
            onClick={onBack ? onBack : () => navigate("/blogs")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-[#5b6f00] transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        {/* ── main card ── */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-8 md:p-10 space-y-8">

          <form onSubmit={handleSubmit} className="space-y-7">

            {/* TITLE */}
            <div>
              <FieldLabel htmlFor="title" hint={derived.slug ? `/${derived.slug}` : ""}>
                Article Title
              </FieldLabel>
              <input
                id="title"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. The Ultimate Guide to Belize"
                required
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3
                           text-lg font-semibold text-gray-900 placeholder:font-normal
                           placeholder:text-gray-400 focus:outline-none
                           focus:ring-2 focus:ring-[#5b6f00]/30 focus:border-[#5b6f00]/40
                           transition"
              />
            </div>

            {/* COVER IMAGE */}
            <div>
              <FieldLabel htmlFor="cover">Cover Image URL</FieldLabel>
              <input
                id="cover"
                name="cover"
                value={form.cover}
                onChange={handleChange}
                placeholder="https://images.unsplash.com/…"
                required
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5
                           text-sm text-gray-800 placeholder:text-gray-400
                           focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/30
                           focus:border-[#5b6f00]/40 transition"
              />
              {form.cover && (
                <img
                  src={form.cover}
                  alt="Cover preview"
                  className="mt-3 w-full h-36 object-cover rounded-xl border border-gray-100"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </div>

            {/* EXCERPT */}
            <div>
              <FieldLabel htmlFor="excerpt" hint="Shown in card + SEO">
                Short Excerpt
              </FieldLabel>
              <textarea
                id="excerpt"
                name="excerpt"
                value={form.excerpt}
                onChange={handleChange}
                rows={3}
                placeholder="A 1–2 sentence summary that hooks the reader…"
                required
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3
                           text-sm text-gray-800 placeholder:text-gray-400 resize-none
                           focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/30
                           focus:border-[#5b6f00]/40 transition"
              />
            </div>

            {/* CONTENT */}
            <div>
              <FieldLabel
                htmlFor="content"
                hint={derived.readTime !== "—" ? `~${derived.readTime}` : ""}
              >
                Article Content
              </FieldLabel>
              <textarea
                id="content"
                name="content"
                value={form.content}
                onChange={handleChange}
                rows={20}
                placeholder={`Write your article here…\n\nFormatting guide:\n• Separate paragraphs with a blank line\n• ## Heading Two\n• # Heading One\n• img:https://image-url.com/photo.jpg\n\nExample:\n\nBelize is a paradise for beach lovers.\n\n## Best Hotels in Ambergris Caye\nThis island is famous for its reef.\n\nimg:https://images.unsplash.com/photo-xxxxx`}
                required
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4
                           text-[15px] leading-relaxed text-gray-800 placeholder:text-gray-400
                           resize-y focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/30
                           focus:border-[#5b6f00]/40 transition font-mono"
              />
            </div>

            {/* AUTO-DERIVED PILLS */}
            {(derived.slug || derived.readTime !== "—") && (
              <div className="flex flex-wrap gap-2">
                {derived.slug && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-600 font-medium">
                    🔗 Slug: <code className="font-mono">{derived.slug}</code>
                  </span>
                )}
                {derived.readTime !== "—" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#5b6f00]/10 text-xs text-[#5b6f00] font-medium">
                    ⏱ {derived.readTime}
                  </span>
                )}
              </div>
            )}

            {/* ACTIONS */}
            <div className="flex items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-[#5b6f00] px-7 py-3
                           text-white text-sm font-semibold shadow-md
                           hover:bg-[#4a5c00] hover:-translate-y-0.5 hover:shadow-lg
                           disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0
                           transition-all duration-200"
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                    Publishing…
                  </>
                ) : (
                  "Publish Article →"
                )}
              </button>

              {form.content && (
                <button
                  type="button"
                  onClick={() => setPreview((p) => !p)}
                  className="text-sm font-medium text-gray-500 hover:text-[#5b6f00] transition-colors"
                >
                  {preview ? "Hide preview" : "Preview article"}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── PREVIEW PANEL ── */}
        {preview && form.content && (
          <div className="mt-8 bg-white rounded-3xl border border-gray-100 shadow-md p-8 md:p-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-6">
              Live Preview
            </p>
            {form.title && (
              <h1 className="text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
                {form.title}
              </h1>
            )}
            {form.cover && (
              <img src={form.cover} alt="" className="w-full rounded-xl object-cover max-h-56 mb-6 border border-gray-100" />
            )}
            <div className="space-y-4">
              {previewBlocks}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminBlog;
